// Shared client-side PDF exporter for tabular reports.
// Uses jsPDF + jsPDF-AutoTable so grid data is exported as real tabular PDF
// (with auto-pagination and column wrapping) instead of a clipped DOM snapshot.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DEFAULT_FOOTER_COLOR = [100, 116, 139];
const DEFAULT_HEADER_FILL  = [43, 108, 176];
const DEFAULT_HEADER_TEXT  = [255, 255, 255];

/**
 * Export one or more tabular sections to a PDF document.
 *
 * @param {Object}   opts
 * @param {string}   opts.title         Big title rendered at top of page 1
 * @param {string}  [opts.subtitle]     Smaller subtitle line (e.g. property + date range)
 * @param {string[]}[opts.meta]         Lines of small grey text under the subtitle (e.g. "Generated: ...")
 * @param {Array}    opts.sections      One or more table sections. See section shape below.
 * @param {string}  [opts.orientation='landscape']  'portrait' | 'landscape'
 * @param {string}  [opts.pageSize='a4']
 * @param {string}  [opts.filename='report.pdf']
 * @param {string}  [opts.footerLeft]   Footer-left text (e.g. property name)
 * @param {boolean} [opts.compact]      Use smaller font + padding for wide tables (>12 cols)
 * @param {boolean} [opts.print]        Open PDF in a new tab with the browser print
 *                                      dialog auto-popping, instead of triggering a
 *                                      Save-As download. Falls back to download if
 *                                      window.open is blocked by the browser.
 *
 * Section shape:
 *   {
 *     heading?:  string,                                    // optional sub-heading before this table
 *     columns:   [{ header: string, dataKey: string, width?: number, halign?: 'left'|'right'|'center' }],
 *     rows:      Array<Object>,                             // each row keyed by column.dataKey
 *     note?:     string,                                    // optional small text under the table
 *   }
 */
export function exportToPdf(opts) {
  const {
    title,
    subtitle = '',
    meta = [],
    sections = [],
    orientation = 'landscape',
    pageSize = 'a4',
    filename = 'report.pdf',
    footerLeft = '',
    compact = false,
    print = false,
  } = opts || {};
  const bodyFontSize = compact ? 6.5 : 8;
  const headFontSize = compact ? 7 : 8;
  const cellPadding  = compact ? 2 : 4;

  const doc = new jsPDF({ orientation, unit: 'pt', format: pageSize });
  // Set PDF metadata so the browser's "Save Print Output As" dialog
  // pre-fills the File Name field. Without a Title in metadata, Chrome's
  // PDF viewer uses the blob URL's UUID (e.g. cc1eb9c0-…) as the tab
  // title, which the OS Save dialog then defaults to (blank in some
  // browsers, the UUID in others).
  const pdfTitle = (filename || 'document.pdf').replace(/\.pdf$/i, '');
  doc.setProperties({
    title: pdfTitle,
    subject: title || pdfTitle,
    author: 'EASEPMS',
    creator: 'EASEPMS',
  });
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = { top: 56, right: 32, bottom: 40, left: 32 };

  // ── Page header (title / subtitle / meta) ──────────────────────────────────
  let cursorY = margin.top;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(20, 30, 60);
  doc.text(String(title || 'Report'), margin.left, cursorY);
  cursorY += 18;

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 80, 110);
    doc.text(String(subtitle), margin.left, cursorY);
    cursorY += 14;
  }
  if (Array.isArray(meta) && meta.length) {
    doc.setFontSize(8.5);
    doc.setTextColor(120, 130, 145);
    for (const line of meta) {
      doc.text(String(line || ''), margin.left, cursorY);
      cursorY += 11;
    }
  }
  cursorY += 6;

  // ── Tables ─────────────────────────────────────────────────────────────────
  sections.forEach((section, idx) => {
    if (idx > 0) cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;
    if (section.heading) {
      // If a heading would overflow, force new page first
      if (cursorY > pageHeight - margin.bottom - 60) {
        doc.addPage();
        cursorY = margin.top;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(43, 108, 176);
      doc.text(String(section.heading), margin.left, cursorY);
      cursorY += 12;
    }

    const cols = (section.columns || []).map((c) => ({
      header: c.header,
      dataKey: c.dataKey,
    }));
    const columnStyles = {};
    (section.columns || []).forEach((c) => {
      const style = {};
      if (typeof c.width === 'number' && c.width > 0) style.cellWidth = c.width;
      if (c.halign) style.halign = c.halign;
      if (Object.keys(style).length) columnStyles[c.dataKey] = style;
    });

    autoTable(doc, {
      startY: cursorY,
      head: [cols.map((c) => c.header)],
      body: (section.rows || []).map((row) =>
        cols.map((c) => {
          const v = row?.[c.dataKey];
          if (v == null) return '';
          return String(v);
        })
      ),
      margin,
      styles: {
        font: 'helvetica',
        fontSize: bodyFontSize,
        cellPadding,
        overflow: 'linebreak',
        valign: 'top',
        textColor: [30, 41, 59],
        lineColor: [203, 213, 224],
        lineWidth: 0.4,
      },
      headStyles: {
        fillColor: DEFAULT_HEADER_FILL,
        textColor: DEFAULT_HEADER_TEXT,
        fontStyle: 'bold',
        fontSize: headFontSize,
        halign: 'left',
        overflow: 'linebreak',
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles,
      didDrawPage: () => {
        // Footer with property + page number on every page
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...DEFAULT_FOOTER_COLOR);
        if (footerLeft) {
          doc.text(String(footerLeft), margin.left, pageHeight - 18);
        }
        const pageStr = `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${doc.internal.getNumberOfPages()}`;
        doc.text(pageStr, pageWidth - margin.right, pageHeight - 18, { align: 'right' });
      },
    });

    if (section.note) {
      const afterY = (doc.lastAutoTable?.finalY || cursorY) + 10;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(String(section.note), margin.left, afterY);
    }
  });

  // Second pass for page numbers — autoTable's didDrawPage stores the
  // page-count at draw time, which can be stale; fix totals on all pages.
  const total = doc.internal.getNumberOfPages();
  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...DEFAULT_FOOTER_COLOR);
    // Clear by overwriting (small white box) then re-render
    const yLine = pageHeight - 18;
    doc.setFillColor(255, 255, 255);
    doc.rect(pageWidth - margin.right - 110, yLine - 9, 110, 12, 'F');
    doc.text(`Page ${p} of ${total}`, pageWidth - margin.right, yLine, { align: 'right' });
  }

  if (print) {
    doc.autoPrint();
    // Wrap the PDF blob as a File so the URL.createObjectURL hint carries
    // the filename. Without this, Chrome's "Save as PDF" print destination
    // uses the blob UUID (e.g. 84f73b58-...) as the default filename even
    // though the PDF metadata Title is set correctly. With the File wrapper,
    // Chrome reads .name and uses it in the Save dialog.
    let url;
    try {
      const blob = doc.output('blob');
      const file = new File([blob], filename, { type: 'application/pdf' });
      url = URL.createObjectURL(file);
    } catch (_) {
      // File constructor not available in some older browsers — fall back
      // to the plain bloburl flow.
      url = doc.output('bloburl');
    }
    const win = window.open(url, '_blank');
    if (!win) {
      // Popup blocked — fall back to download so the user still gets the file.
      doc.save(filename);
    } else {
      // Free the object URL once the new window finishes loading. 60s is
      // generous; the print/save dialog usually closes well before that.
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
    }
  } else {
    doc.save(filename);
  }
}
